import Badge from '@/components/base/Badge';
import Button from '@/components/base/Button';
import type { MidiDeviceInfo } from '@/lib/input/midi';
import type { MidiStatus } from '@/hooks/useMidiInput';

interface MidiPanelProps {
  supported: boolean;
  status: MidiStatus;
  devices: MidiDeviceInfo[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  onConnect: () => void;
  onDisconnect: () => void;
}

const STATUS_LABEL: Record<MidiStatus, string> = {
  unsupported: 'Sem suporte neste navegador',
  idle: 'Desconectado',
  requesting: 'Aguardando permissão...',
  connected: 'Conectado',
  denied: 'Nenhum dispositivo encontrado',
};

export default function MidiPanel({
  supported,
  status,
  devices,
  selectedId,
  onSelect,
  onConnect,
  onDisconnect,
}: MidiPanelProps) {
  const connected = status === 'connected';

  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="w-9 h-9 flex items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
            <i className="ri-usb-line text-base" />
          </span>
          <div>
            <div className="font-label text-sm font-semibold text-foreground-900">
              Teclado musical USB / MIDI
            </div>
            <div className="text-xs text-foreground-500">
              Opcional. O teclado virtual na tela continua funcionando sem isso.
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Badge tone={connected ? 'accent' : 'neutral'}>
            {STATUS_LABEL[status]}
          </Badge>
          {connected ? (
            <Button variant="outline" size="sm" icon="ri-link-unlink" onClick={onDisconnect}>
              Desconectar
            </Button>
          ) : (
            <Button
              variant="secondary"
              size="sm"
              icon="ri-plug-line"
              onClick={onConnect}
              disabled={!supported || status === 'requesting'}
            >
              Conectar teclado
            </Button>
          )}
        </div>
      </div>

      {!supported ? (
        <p className="mt-3 text-xs text-foreground-500">
          Este navegador não oferece suporte à entrada MIDI. Você pode continuar praticando com o
          teclado virtual.
        </p>
      ) : null}

      {supported && devices.length > 0 ? (
        <div className="mt-3">
          <label
            htmlFor="midi-device"
            className="mb-1 block font-label text-xs font-semibold text-foreground-600"
          >
            Dispositivo
          </label>
          <select
            id="midi-device"
            value={selectedId ?? ''}
            onChange={(event) => onSelect(event.target.value)}
            className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-300"
          >
            {devices.map((device) => (
              <option key={device.id} value={device.id}>
                {device.name}
                {device.manufacturer ? ` — ${device.manufacturer}` : ''}
              </option>
            ))}
          </select>
        </div>
      ) : null}

      {supported && status === 'denied' ? (
        <p className="mt-3 text-xs text-foreground-500">
          Nenhum teclado MIDI foi encontrado. Conecte o cabo USB e tente novamente.
        </p>
      ) : null}
    </div>
  );
}